import { useState } from "react";
import {
  Avatar,
  Card,
  Divider,
  Flex,
  Text,
  useMantineTheme,
  Button,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { generateSlug } from "../../../../utils/cleanSlug";
import { EPredictiveType } from "../mock";
import { AddCircle, Profile } from "iconsax-react";
import ModalBuy, { ISelectedOption } from "../ModalBuy";
import {
  EInstrumentType,
  ETypeInstrument,
  IListPredictive,
} from "../../../../hooks/usePredictives";
import { meetingDate } from "../../../../utils/date";

interface IProps {
  data: IListPredictive;
  preSlug?: string;
}

export default function PredictiveCard({ data, preSlug }: IProps) {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalBuy, setIsModalBuy] = useState<ISelectedOption | null>(null);

  const handleNavigate = () => {
    const preSlugTemp = !!preSlug ? preSlug : "/marketplace/predix/detalhes/";
    navigate(
      `${preSlugTemp}${generateSlug(data.title)}&id=${data.id.toString()}`,
      {
        preventScrollReset: false,
      }
    );
  };

  const handleBuy = (props: ISelectedOption) => {
    setIsModalBuy(props);
  };

  return (
    <>
      {isModalBuy ? (
        <ModalBuy isModalBuy={isModalBuy} setIsModalBuy={setIsModalBuy} />
      ) : null}
      <Card
        withBorder
        p={5}
        miw={"100%"}
        mih={170}
        sx={(theme) => ({
          textDecoration: "none",
          cursor: "auto",
          transition: "all 0.5s ease-in-out",
          borderRadius: "12px",
          boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          background: "white",
          border: "1px solid #D7D7D7",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: "5px",
        })}
      >
        <Flex
          direction={"column"}
          h="fit-content"
          justify="start"
          w="100%"
          gap={"8px"}
          p="10px"
        >
          <Text
            fz="14px"
            fw={500}
            color="basic.4"
            ff="Montserrat"
            lh="10px"
            style={{
              textDecoration: "underline",
            }}
          >
            {meetingDate({
              date: data.expiration,
              category: data.category,
              t,
            })}
          </Text>
          <Flex align={"center"} gap="10px" w="100%">
            <Avatar
              variant="outline"
              size={38}
              color="basic.2"
              radius={4}
              src={data?.icon}
              alt={!!data?.title ? data.title : "--"}
              styles={{
                root: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1.5px solid ${theme.colors.basic[2]}`,
                },
                image: {
                  height: "38px",
                  width: "38px",
                },
              }}
            >
              <Profile color={theme.colors.basic[5]} />
            </Avatar>
            <Text fz="14px" fw={600} color="basic.5" ff="Montserrat" lh="136%">
              {data.title}
            </Text>
          </Flex>
        </Flex>
        <Flex direction={"column"} h="100%" justify="end" w="100%" gap={"7px"}>
          {data.type === EPredictiveType.binary && (
            <Flex
              px="12px"
              gap="10px"
              justify={"space-between"}
              align={"center"}
            >
              <Button
                onClick={() =>
                  handleBuy({
                    data,
                    selected: {
                      id: data.instruments[0].id,
                      side: ETypeInstrument.BUY,
                    },
                  })
                }
                variant="optionYes"
                styles={{
                  root: {
                    width: "100%",
                    minHeight: "27px !important",
                  },
                  label: {
                    fontSize: "13px",
                    lineHeight: "13px !important",
                    height: "13px !important",
                  },
                  inner: {
                    height: "13px !important",
                  },
                }}
              >
                {`${t("global.predictives.yes")} ${
                  data.instruments[0].ask_price_formatted
                }`}
              </Button>
              <Button
                onClick={() =>
                  handleBuy({
                    data,
                    selected: {
                      id: data.instruments[0].id,
                      side: ETypeInstrument.SELL,
                    },
                  })
                }
                variant="optionNo"
                styles={{
                  root: {
                    width: "100%",
                    minHeight: "27px !important",
                  },
                  label: {
                    fontSize: "13px",
                    lineHeight: "13px !important",
                    height: "13px !important",
                  },
                  inner: {
                    height: "13px !important",
                  },
                }}
              >
                {`${t("global.predictives.no")} ${
                  data.instruments[0].calculated_ask_formatted
                }`}
              </Button>
            </Flex>
          )}

          {data.type === EPredictiveType.multi && (
            <Flex
              direction={"column"}
              gap="5px"
              px="12px"
              h="100%"
              justify={"center"}
            >
              {!!data.instruments &&
                data.instruments.map((instrument, index) => {
                  return (
                    <Flex
                      key={`${instrument.label}-${index}`}
                      justify={"space-between"}
                      align={"center"}
                      gap="8px"
                    >
                      <Text
                        w="fit-content"
                        fz="12px"
                        fw={600}
                        color={
                          instrument.direction === EInstrumentType.increase
                            ? theme.colors.complementary[0]
                            : instrument.direction === EInstrumentType.neutral
                            ? theme.colors.complementary[7]
                            : theme.colors.complementary[2]
                        }
                        ff="Montserrat"
                        lh="16px"
                        miw={"100px"}
                        style={{
                          whiteSpace: "wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {instrument.label}
                      </Text>
                      <Flex align={"center"} w="100%" h="100%">
                        <Button
                          onClick={() =>
                            handleBuy({
                              data,
                              selected: {
                                id: instrument.id,
                                side: ETypeInstrument.BUY,
                              },
                            })
                          }
                          w="100%"
                          variant="optionYes"
                          style={{
                            minWidth: "65px",
                          }}
                          styles={{
                            root: {
                              height: "100%",
                              flex: "1",
                              borderRadius: "4px 0 0 4px !important",
                              padding: "4px !important",
                            },
                            label: {
                              fontSize: "13px",
                              lineHeight: "16px !important",
                            },
                          }}
                        >
                          {`${t("global.predictives.yes")} ${
                            instrument.ask_price_formatted
                          }`}
                        </Button>
                        <Button
                          onClick={() =>
                            handleBuy({
                              data,
                              selected: {
                                id: instrument.id,
                                side: ETypeInstrument.SELL,
                              },
                            })
                          }
                          variant="optionNo"
                          style={{
                            minWidth: "65px",
                          }}
                          w="100%"
                          styles={{
                            root: {
                              height: "100%",
                              flex: "1",
                              borderRadius: "0 4px 4px 0 !important",
                              padding: "4px !important",
                            },
                            label: {
                              fontSize: "13px",
                              lineHeight: "16px !important",
                            },
                          }}
                        >
                          {`${t("global.predictives.no")} ${
                            instrument.calculated_ask_formatted
                          }`}
                        </Button>
                      </Flex>
                    </Flex>
                  );
                })}
            </Flex>
          )}

          <Divider />

          <Flex
            w="100%"
            gap="10px"
            px="12px"
            align={"center"}
            justify={"space-between"}
            style={{
              cursor: "pointer",
            }}
            onClick={handleNavigate}
          >
            <Text fz="12px" fw={600} color="basic.4" ff="Montserrat" lh="16px">
              {data.type === EPredictiveType.multi
                ? t("global.predictives.card.qty_options", {
                    qty: data.instruments_count - data.instruments.length,
                  })
                : t("global.predictives.card.see_page")}
            </Text>
            <AddCircle size="20px" color="#6B6B6B" />
          </Flex>
        </Flex>
      </Card>
    </>
  );
}
